<script lang="ts">
// Module-level counter: unique title ids across drawer instances.
let drawerCount = 0;

// Module-level body scroll lock (refcount) — multiple modal drawers share one overflow save/restore.
let scrollLockCount = 0;
let savedBodyOverflow: string | null = null;

// Module-level stack of open drawer instances — Escape closes only the top-most drawer.
type DrawerEscapeHandler = { onEscapeKey: () => void };
const openDrawerStack: DrawerEscapeHandler[] = [];
</script>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  useSlots,
  watch,
  type VNode,
} from 'vue';
import { getFocusableElements, trapTabKey } from './focus';
import { loadingStateInjectionKey } from '../Inputs/internal';
import { useTableFocusClaim } from '../Table/focusScope';

export type NxDrawerPosition = 'left' | 'right' | 'top' | 'bottom';

const props = withDefaults(
  defineProps<{
    position?: NxDrawerPosition;
    /** CSS length; width for left/right, height for top/bottom. */
    size?: string;
    title?: string;
    subtitle?: string;
    /** true: backdrop + blocks behind; false: modeless (no backdrop). */
    modal?: boolean;
    closeOnEscape?: boolean;
    /** Modal only. */
    closeOnBackdrop?: boolean;
    /** Close (✕) button in the header. */
    showClose?: boolean;
    /** aria-label of the ✕ button — nx-ui carries no i18n, pass localized copy. */
    closeLabel?: string;
    /** Accessible-name fallback when the header slot replaces the title. */
    ariaLabel?: string;
    /** false (default): body stays mounted while closed — chat history survives. */
    unmountOnClose?: boolean;
    teleportTo?: string;
    /**
     * The `#footer` slot brings its own padding and divider (a full-width
     * `NxChatComposer`): the drawer adds neither, so the footer runs edge to
     * edge without a second line above it.
     */
    flushFooter?: boolean;
  }>(),
  {
    position: 'right',
    size: '25rem',
    title: '',
    subtitle: '',
    modal: true,
    closeOnEscape: true,
    closeOnBackdrop: true,
    showClose: true,
    closeLabel: 'Close',
    ariaLabel: '',
    unmountOnClose: false,
    teleportTo: 'body',
    flushFooter: false,
  }
);

const open = defineModel<boolean>('open', { required: true });
const emit = defineEmits<{ close: []; opened: []; closed: [] }>();
defineSlots<{
  /** Scrollable drawer body. */
  default?: () => VNode;
  /** Replaces the title/subtitle row (keep an ariaLabel when you use this). */
  header?: () => VNode;
  /** Context line under the header (e.g. "Kontext: …"). */
  context?: () => VNode;
  /** Pinned footer (e.g. a chat input row). */
  footer?: () => VNode;
}>();

const slots = useSlots();
const panelRef = ref<HTMLElement | null>(null);
// Opened from a table, it belongs to that table's editing wherever it renders.
useTableFocusClaim(panelRef, () => open.value);
// A dialog of its own error context, like NxModal: its inputs never take the save status of a
// field the drawer sits in.
provide(loadingStateInjectionKey, null);
const lastFocused = ref<HTMLElement | null>(null);
let hasLock = false;
let isMounted = false;

const escapeHandler: DrawerEscapeHandler = {
  onEscapeKey: () => {
    if (props.closeOnEscape) requestClose();
  },
};

const titleId = `nx-drawer-title-${++drawerCount}`;
const labelledBy = computed(() => (props.title && !slots.header ? titleId : undefined));
const fallbackLabel = computed(() => (labelledBy.value ? undefined : props.ariaLabel || undefined));

function requestClose() {
  open.value = false;
  emit('close');
}

function warnIfMissingAccessibleName() {
  if (!import.meta.env.DEV) return;
  const hasDefaultTitle = props.title && !slots.header;
  if (!hasDefaultTitle && !props.ariaLabel) {
    console.warn('[NxDrawer] provide a title or an ariaLabel for an accessible name.');
  }
}

function focusInitial() {
  if (!props.modal) return;
  const panel = panelRef.value;
  if (!panel) return;

  const focusables = getFocusableElements(panel);
  if (focusables.length > 0) {
    focusables[0].focus();
  } else {
    panel.focus();
  }
}

function restoreFocus() {
  const target = lastFocused.value;
  lastFocused.value = null;
  if (!target || typeof document === 'undefined') return;

  if (document.contains(target)) {
    try {
      target.focus();
    } catch {
      // Detached or otherwise unfocusable — no-op.
    }
  }
}

function lockScroll() {
  if (typeof document === 'undefined' || hasLock) return;
  if (scrollLockCount === 0) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLockCount++;
  hasLock = true;
}

function unlockScroll() {
  if (typeof document === 'undefined' || !hasLock) return;
  scrollLockCount--;
  hasLock = false;
  if (scrollLockCount === 0 && savedBodyOverflow !== null) {
    document.body.style.overflow = savedBodyOverflow;
    savedBodyOverflow = null;
  }
}

function pushEscapeStack() {
  openDrawerStack.push(escapeHandler);
}

function popEscapeStack() {
  const idx = openDrawerStack.indexOf(escapeHandler);
  if (idx !== -1) openDrawerStack.splice(idx, 1);
}

function onKeydown(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  const top = openDrawerStack[openDrawerStack.length - 1];
  if (top !== escapeHandler) return;
  escapeHandler.onEscapeKey();
}

function onPanelKeydown(e: KeyboardEvent) {
  if (!props.modal || !open.value || e.key !== 'Tab') return;
  const panel = panelRef.value;
  if (panel) trapTabKey(e, panel);
}

function onOpen() {
  warnIfMissingAccessibleName();
  if (typeof document === 'undefined') return;

  lastFocused.value = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  document.addEventListener('keydown', onKeydown);
  pushEscapeStack();
  if (props.modal) lockScroll();
}

function onClose() {
  if (typeof document === 'undefined') return;

  document.removeEventListener('keydown', onKeydown);
  popEscapeStack();
  if (hasLock) unlockScroll();
}

watch(open, (isOpen) => {
  if (!isMounted || typeof document === 'undefined') return;
  if (isOpen) onOpen();
  else onClose();
});

watch(
  () => props.modal,
  (isModal) => {
    if (!isMounted || !open.value || typeof document === 'undefined') return;
    if (isModal) lockScroll();
    else unlockScroll();
  }
);

onMounted(() => {
  isMounted = true;
  if (open.value) onOpen();
});

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('keydown', onKeydown);
    popEscapeStack();
  }
  unlockScroll();
  restoreFocus();
});

const sizeStyle = computed(() => ({ '--nxt-drawer-size': props.size }));

function onAfterEnter() {
  emit('opened');
  if (props.modal) {
    nextTick(() => {
      const panel = panelRef.value;
      const active = document.activeElement;
      if (panel && active instanceof Node && panel.contains(active)) return;
      focusInitial();
    });
  }
}

function onAfterLeave() {
  restoreFocus();
  emit('closed');
}
</script>

<template>
  <Teleport :to="teleportTo">
    <Transition name="nx-drawer-fade">
      <div
        v-if="modal && open"
        class="nx-drawer__backdrop"
        aria-hidden="true"
        @click="closeOnBackdrop && requestClose()"
      />
    </Transition>
    <Transition
      :name="`nx-drawer-slide-${position}`"
      @after-enter="onAfterEnter"
      @after-leave="onAfterLeave"
    >
      <aside
        v-if="unmountOnClose ? open : true"
        v-show="unmountOnClose ? true : open"
        ref="panelRef"
        class="nx-drawer"
        :class="`nx-drawer--${position}`"
        :style="sizeStyle"
        role="dialog"
        :tabindex="modal ? -1 : undefined"
        :aria-modal="modal ? 'true' : undefined"
        :aria-labelledby="labelledBy"
        :aria-label="fallbackLabel"
        @keydown="onPanelKeydown"
      >
        <header class="nx-drawer__head">
          <slot name="header">
            <div class="nx-drawer__titles">
              <div :id="titleId" class="nx-drawer__title">{{ title }}</div>
              <div v-if="subtitle" class="nx-drawer__subtitle">{{ subtitle }}</div>
            </div>
          </slot>
          <button
            v-if="showClose"
            type="button"
            class="nx-drawer__close"
            :aria-label="closeLabel"
            @click="requestClose"
          >
            ✕
          </button>
        </header>
        <div v-if="$slots.context" class="nx-drawer__context">
          <slot name="context" />
        </div>
        <div class="nx-drawer__body">
          <slot />
        </div>
        <footer
          v-if="$slots.footer"
          class="nx-drawer__footer"
          :class="{ 'nx-drawer__footer--flush': flushFooter }"
        >
          <slot name="footer" />
        </footer>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* No <dialog>: drawers must not force the browser top layer (the shell FAB and
   toasts stay reachable). Stacking via the nx-ui z tokens (below NxToastHost's
   hardcoded 10000, which deliberately stays untouched). */
.nx-drawer__backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--nxt-z-drawer-backdrop);
  background: var(--nxt-drawer-overlay-bg);
}
.nx-drawer {
  position: fixed;
  z-index: var(--nxt-z-drawer);
  display: flex;
  flex-direction: column;
  background: var(--nxt-drawer-bg);
  /* Teleported to <body>: never inherit the page's text colour. */
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  box-shadow: var(--nxt-shadow-lg);
  border: var(--nxt-hairline) solid var(--nxt-drawer-border);
}
.nx-drawer--left,
.nx-drawer--right {
  top: 0;
  bottom: 0;
  width: var(--nxt-drawer-size);
  max-width: 92vw;
}
.nx-drawer--left {
  left: 0;
  border-left: none;
}
.nx-drawer--right {
  right: 0;
  border-right: none;
}
.nx-drawer--top,
.nx-drawer--bottom {
  left: 0;
  right: 0;
  height: var(--nxt-drawer-size);
  max-height: 92vh;
}
.nx-drawer--top {
  top: 0;
  border-top: none;
}
.nx-drawer--bottom {
  bottom: 0;
  border-bottom: none;
}
.nx-drawer__head {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-md);
  border-bottom: var(--nxt-hairline) solid var(--nxt-drawer-border);
}
.nx-drawer__titles {
  flex: 1;
  min-width: 0;
}
.nx-drawer__title {
  font-size: var(--nxt-font-size-lg);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  color: var(--nxt-fg);
}
.nx-drawer__subtitle {
  margin-block-start: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}
.nx-drawer__close {
  flex-shrink: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: var(--nxt-font-size-md);
  color: var(--nxt-fg-muted);
  padding: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
}
.nx-drawer__close:hover {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}
.nx-drawer__context {
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  border-bottom: var(--nxt-hairline) solid var(--nxt-drawer-border);
  background: var(--nxt-surface-sunken-bg);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-drawer__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--nxt-space-md);
}
/* The global type reset sizes bare <p>/<ul> at 16px medium; the drawer sets it. */
.nx-drawer__body :deep(:is(p, ul, ol)) {
  font-size: inherit;
  font-weight: inherit;
}
.nx-drawer__footer {
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-top: var(--nxt-hairline) solid var(--nxt-drawer-border);
}
.nx-drawer__footer.nx-drawer__footer--flush {
  padding: 0;
  border-top: 0;
}

/* Transitions per position (transform slide; backdrop fades). */
.nx-drawer-fade-enter-active,
.nx-drawer-fade-leave-active {
  transition: opacity var(--nxt-motion-base) ease;
}
.nx-drawer-fade-enter-from,
.nx-drawer-fade-leave-to {
  opacity: 0;
}
.nx-drawer-slide-left-enter-active,
.nx-drawer-slide-left-leave-active,
.nx-drawer-slide-right-enter-active,
.nx-drawer-slide-right-leave-active,
.nx-drawer-slide-top-enter-active,
.nx-drawer-slide-top-leave-active,
.nx-drawer-slide-bottom-enter-active,
.nx-drawer-slide-bottom-leave-active {
  transition: transform var(--nxt-motion-base) ease;
}
.nx-drawer-slide-left-enter-from,
.nx-drawer-slide-left-leave-to {
  transform: translateX(-100%);
}
.nx-drawer-slide-right-enter-from,
.nx-drawer-slide-right-leave-to {
  transform: translateX(100%);
}
.nx-drawer-slide-top-enter-from,
.nx-drawer-slide-top-leave-to {
  transform: translateY(-100%);
}
.nx-drawer-slide-bottom-enter-from,
.nx-drawer-slide-bottom-leave-to {
  transform: translateY(100%);
}
</style>
